import React, { useState, useEffect } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert,
  StepButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  PersonAdd,
  HomeWork,
  Description,
  CheckCircle,
  NavigateNext,
  NavigateBefore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useClientStore } from '../stores/useClientStore';
import { useProjetStore } from '../stores/useProjetStore';
import { useNotification } from '../contexts/NotificationContext';
import { StatutProjet } from '../types';
import { extractErrorMessage } from '../utils/errorUtils';
import AddressAutocomplete from './AddressAutocomplete';

const steps = [
  { label: 'Sélectionner un client', icon: <PersonAdd />, description: 'Choisissez ou créez un client' },
  { label: 'Sélectionner un projet', icon: <HomeWork />, description: 'Choisissez ou créez un projet' },
  { label: 'Créer le devis', icon: <Description />, description: 'Remplissez les informations du devis' },
];

interface DevisWizardProps {
  onComplete: (clientId: number, projetId: number) => void;
  initialProjetId?: number | null;
}

const DevisWizard: React.FC<DevisWizardProps> = ({ onComplete, initialProjetId }) => {
  const navigate = useNavigate();
  const { clients, loadClients, addClient } = useClientStore();
  const { projets, loadProjets, loadProjetsByClientId, addProjet, getProjetById } = useProjetStore();
  const { showSuccess, showError } = useNotification();

  const [activeStep, setActiveStep] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedProjetId, setSelectedProjetId] = useState<number | null>(initialProjetId || null);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showProjetDialog, setShowProjetDialog] = useState(false);

  // État pour la création de client
  const [newClient, setNewClient] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    codePostal: '',
    ville: '',
  });

  // État pour la création de projet
  const [newProjet, setNewProjet] = useState({
    titre: '',
    description: '',
    adresseChantier: '',
    codePostal: '',
    ville: '',
  });

  const [dialogError, setDialogError] = useState<string | null>(null);

  // Charger les données au montage
  useEffect(() => {
    loadClients();
    loadProjets();
  }, [loadClients, loadProjets]);

  // Pré-sélectionner le client si un projet est fourni
  useEffect(() => {
    if (initialProjetId) {
      const projet = getProjetById(initialProjetId);
      if (projet) {
        setSelectedClientId(projet.clientId);
        setSelectedProjetId(initialProjetId);
        // Passer directement à l'étape 3 (création du devis)
        setActiveStep(2);
      }
    } else {
      // Toujours commencer à l'étape 0 (sélection client) par défaut
      setActiveStep(0);
    }
  }, [initialProjetId, getProjetById]);

  // Filtrer les projets par client sélectionné
  const filteredProjets = selectedClientId
    ? projets.filter((p) => p.clientId === selectedClientId)
    : projets;

  const handleNext = () => {
    // Validation avant de passer à l'étape suivante
    if (activeStep === 0 && !selectedClientId) {
      showError('Veuillez sélectionner ou créer un client');
      return;
    }
    if (activeStep === 1 && !selectedProjetId) {
      showError('Veuillez sélectionner ou créer un projet');
      return;
    }

    if (activeStep === 2) {
      // Dernière étape : déclencher la création du devis
      if (selectedClientId && selectedProjetId) {
        onComplete(selectedClientId, selectedProjetId);
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    // Permettre la navigation uniquement vers les étapes déjà validées
    if (step === 0 || (step === 1 && selectedClientId) || (step === 2 && selectedClientId && selectedProjetId)) {
      setActiveStep(step);
    }
  };

  // Gestion de la création de client
  const handleOpenClientDialog = () => {
    setNewClient({
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      adresse: '',
      codePostal: '',
      ville: '',
    });
    setDialogError(null);
    setShowClientDialog(true);
  };

  const handleCloseClientDialog = () => {
    setShowClientDialog(false);
    setDialogError(null);
  };

  const handleCreateClient = async () => {
    // Validation
    if (!newClient.prenom.trim() || !newClient.nom.trim()) {
      setDialogError('Le prénom et le nom sont obligatoires');
      return;
    }
    if (!newClient.email.trim()) {
      setDialogError("L'email est obligatoire");
      return;
    }
    if (!newClient.telephone.trim()) {
      setDialogError('Le téléphone est obligatoire');
      return;
    }

    try {
      // Concaténer l'adresse complète
      const adresseComplete = newClient.adresse && newClient.codePostal && newClient.ville
        ? `${newClient.adresse}, ${newClient.codePostal} ${newClient.ville}`
        : '';

      const createdClient = await addClient({
        prenom: newClient.prenom,
        nom: newClient.nom,
        email: newClient.email,
        telephone: newClient.telephone,
        adresse: adresseComplete,
        ville: newClient.ville,
        codePostal: newClient.codePostal,
      });

      setSelectedClientId(createdClient.id);
      showSuccess('Client créé avec succès');
      handleCloseClientDialog();

      // Charger les projets de ce client
      loadProjetsByClientId(createdClient.id);

      // Passer automatiquement à l'étape suivante
      setActiveStep(1);
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création du client');
      setDialogError(errorMessage);
    }
  };

  // Gestion de la création de projet
  const handleOpenProjetDialog = () => {
    setNewProjet({
      titre: '',
      description: '',
      adresseChantier: '',
      codePostal: '',
      ville: '',
    });
    setDialogError(null);
    setShowProjetDialog(true);
  };

  const handleCloseProjetDialog = () => {
    setShowProjetDialog(false);
    setDialogError(null);
  };

  const handleCreateProjet = async () => {
    // Validation
    if (!newProjet.titre.trim()) {
      setDialogError('Le titre du projet est obligatoire');
      return;
    }
    if (!newProjet.adresseChantier.trim()) {
      setDialogError("L'adresse du chantier est obligatoire");
      return;
    }
    if (!newProjet.codePostal.trim()) {
      setDialogError('Le code postal est obligatoire');
      return;
    }
    if (!newProjet.ville.trim()) {
      setDialogError('La ville est obligatoire');
      return;
    }
    if (!selectedClientId) {
      setDialogError('Aucun client sélectionné');
      return;
    }

    try {
      // Concaténer l'adresse complète
      const adresseComplete = `${newProjet.adresseChantier}, ${newProjet.codePostal} ${newProjet.ville}`;

      const createdProjet = await addProjet({
        titre: newProjet.titre,
        description: newProjet.description,
        adresseChantier: adresseComplete,
        clientId: selectedClientId,
        statut: StatutProjet.EN_ATTENTE_DEVIS,
      });

      setSelectedProjetId(createdProjet.id);
      showSuccess('Projet créé avec succès');
      handleCloseProjetDialog();

      // Passer automatiquement à l'étape suivante
      setActiveStep(2);
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de la création du projet');
      setDialogError(errorMessage);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
              Sélectionnez un client
            </Typography>

            {clients.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                Aucun client trouvé. Créez votre premier client pour commencer.
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 3 }}>
                {clients.length} client{clients.length > 1 ? 's' : ''} disponible{clients.length > 1 ? 's' : ''}. Sélectionnez-en un ou créez-en un nouveau.
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Client</InputLabel>
              <Select
                value={selectedClientId || ''}
                onChange={(e) => setSelectedClientId(e.target.value ? Number(e.target.value) : null)}
                label="Client"
              >
                <MenuItem value="">
                  <em>Sélectionnez un client</em>
                </MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.prenom} {client.nom} - {client.ville || client.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={handleOpenClientDialog}
              fullWidth
              sx={{ borderColor: '#10B981', color: '#10B981', '&:hover': { borderColor: '#059669', bgcolor: '#D1FAE5' } }}
            >
              Créer un nouveau client
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
              Sélectionnez un projet
            </Typography>

            {selectedClientId && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
                Client sélectionné : <strong>{clients.find(c => c.id === selectedClientId)?.prenom} {clients.find(c => c.id === selectedClientId)?.nom}</strong>
              </Alert>
            )}

            {filteredProjets.length === 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Aucun projet trouvé pour ce client. Créez un nouveau projet.
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Projet</InputLabel>
              <Select
                value={selectedProjetId || ''}
                onChange={(e) => setSelectedProjetId(e.target.value ? Number(e.target.value) : null)}
                label="Projet"
                disabled={!selectedClientId}
              >
                <MenuItem value="">
                  <em>Sélectionnez un projet</em>
                </MenuItem>
                {filteredProjets.map((projet) => (
                  <MenuItem key={projet.id} value={projet.id}>
                    {projet.titre} - {projet.adresseChantier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<HomeWork />}
              onClick={handleOpenProjetDialog}
              fullWidth
              disabled={!selectedClientId}
              sx={{ borderColor: '#3B82F6', color: '#3B82F6', '&:hover': { borderColor: '#2563EB', bgcolor: '#DBEAFE' } }}
            >
              Créer un nouveau projet
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#374151', mb: 3 }}>
              Prêt à créer le devis
            </Typography>

            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Client :</strong> {clients.find(c => c.id === selectedClientId)?.prenom} {clients.find(c => c.id === selectedClientId)?.nom}
              </Typography>
              <Typography variant="body2">
                <strong>Projet :</strong> {projets.find(p => p.id === selectedProjetId)?.titre}
              </Typography>
            </Alert>

            <Alert severity="info">
              Cliquez sur "Créer le devis" pour accéder au formulaire de création.
            </Alert>
          </Box>
        );

      default:
        return 'Étape inconnue';
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 4, mb: 4, boxShadow: 2, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
          Assistant de création de devis
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Suivez les étapes pour créer votre devis
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepButton onClick={() => handleStepClick(index)}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: activeStep >= index ? '#F59E0B' : '#E5E7EB',
                        color: activeStep >= index ? 'white' : '#9CA3AF',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4 }}>
          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<NavigateBefore />}
              sx={{ mr: 1 }}
            >
              Retour
            </Button>

            <Box sx={{ flex: '1 1 auto' }} />

            <Button
              variant="outlined"
              onClick={() => navigate('/devis')}
              sx={{ mr: 2 }}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={activeStep === 2 ? <Description /> : <NavigateNext />}
              sx={{
                bgcolor: '#F59E0B',
                '&:hover': { bgcolor: '#D97706' },
              }}
              disabled={
                (activeStep === 0 && !selectedClientId) ||
                (activeStep === 1 && !selectedProjetId)
              }
            >
              {activeStep === 2 ? 'Créer le devis' : 'Suivant'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Dialog de création de client */}
      <Dialog open={showClientDialog} onClose={handleCloseClientDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un nouveau client</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Prénom *"
              value={newClient.prenom}
              onChange={(e) => setNewClient({ ...newClient, prenom: e.target.value })}
            />
            <TextField
              fullWidth
              label="Nom *"
              value={newClient.nom}
              onChange={(e) => setNewClient({ ...newClient, nom: e.target.value })}
            />
          </Box>

          <TextField
            fullWidth
            label="Email *"
            type="email"
            value={newClient.email}
            onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth
            label="Téléphone *"
            value={newClient.telephone}
            onChange={(e) => setNewClient({ ...newClient, telephone: e.target.value })}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            <AddressAutocomplete
              label="Adresse"
              value={newClient.adresse}
              onChange={(value) => setNewClient({ ...newClient, adresse: value })}
              onAddressSelect={(address) => {
                setNewClient({
                  ...newClient,
                  adresse: address.adresse,
                  codePostal: address.codePostal,
                  ville: address.ville,
                });
              }}
              placeholder="Ex: 10 Rue de la Paix"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Ville"
              value={newClient.ville}
              onChange={(e) => setNewClient({ ...newClient, ville: e.target.value })}
            />
            <TextField
              label="Code postal"
              value={newClient.codePostal}
              onChange={(e) => setNewClient({ ...newClient, codePostal: e.target.value })}
              sx={{ width: '200px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClientDialog}>Annuler</Button>
          <Button onClick={handleCreateClient} variant="contained" color="primary">
            Créer le client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de création de projet */}
      <Dialog open={showProjetDialog} onClose={handleCloseProjetDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Créer un nouveau projet</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Titre du projet *"
            value={newProjet.titre}
            onChange={(e) => setNewProjet({ ...newProjet, titre: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />

          <TextField
            fullWidth
            label="Description"
            value={newProjet.description}
            onChange={(e) => setNewProjet({ ...newProjet, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <AddressAutocomplete
            label="Adresse du chantier *"
            value={newProjet.adresseChantier}
            onChange={(value) => setNewProjet({ ...newProjet, adresseChantier: value })}
            onAddressSelect={(address) => {
              setNewProjet({
                ...newProjet,
                adresseChantier: address.adresse,
                codePostal: address.codePostal,
                ville: address.ville,
              });
            }}
            placeholder="Ex: 8 Boulevard du Port"
            required
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Ville *"
              value={newProjet.ville}
              onChange={(e) => setNewProjet({ ...newProjet, ville: e.target.value })}
            />
            <TextField
              label="Code postal *"
              value={newProjet.codePostal}
              onChange={(e) => setNewProjet({ ...newProjet, codePostal: e.target.value })}
              sx={{ width: '200px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProjetDialog}>Annuler</Button>
          <Button onClick={handleCreateProjet} variant="contained" color="primary">
            Créer le projet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevisWizard;
